/**
 * DataPrivacySettings Component
 * Admin page for organization and per-program privacy policy management.
 *
 * Tab 1: Organization Defaults — master toggles, tier threshold, re-auth, encryption, break glass
 * Tab 2: Per-Program Privacy — select a program, view/override its policy
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  Lock,
  Clock,
  Eye,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  ChevronRight,
  Info,
  Settings2,
  Key,
  RotateCw,
  Loader2,
  XCircle,
  LogOut,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  privacyAPI,
  OrgPrivacyPolicy,
  OrgPrivacyPolicyUpdate,
  ProgramPrivacyPolicy,
  ProgramPrivacyPolicyUpdate,
  ProgramPrivacySummaryItem,
  SensitivityTier,
  SENSITIVITY_TIERS,
  TIER_LABELS,
  EncryptionKeyStatus,
} from '@/services/api/privacy';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  IDLE_TIMEOUT_CHANGED_EVENT,
  DEFAULT_IDLE_TIMEOUT_MINUTES,
  MAX_IDLE_TIMEOUT_MINUTES,
  MIN_IDLE_TIMEOUT_MINUTES,
  isValidIdleTimeoutMinutes,
} from '@/lib/sessionIdleTimeout';

interface DataPrivacySettingsProps {
  organizationId: number;
  organizationName: string;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function DataPrivacySettings({ organizationId, organizationName }: DataPrivacySettingsProps) {
  const { fmt } = useFormattedDate();
  const [orgPolicy, setOrgPolicy] = useState<OrgPrivacyPolicy | null>(null);
  const [programSummary, setProgramSummary] = useState<ProgramPrivacySummaryItem[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [programPolicy, setProgramPolicy] = useState<ProgramPrivacyPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // -----------------------------------------------------------------------
  // DATA FETCHING
  // -----------------------------------------------------------------------

  const fetchOrgPolicy = useCallback(async () => {
    try {
      const data = await privacyAPI.getOrgPrivacyPolicy(organizationId);
      setOrgPolicy(data);
    } catch (err: any) {
      console.error('Error fetching org privacy policy:', err);
      setError('Failed to load organization privacy policy');
    }
  }, [organizationId]);

  const fetchProgramSummary = useCallback(async () => {
    try {
      const data = await privacyAPI.getProgramsPrivacySummary(organizationId);
      setProgramSummary(data);
    } catch (err: any) {
      console.error('Error fetching program privacy summary:', err);
    }
  }, [organizationId]);

  const fetchProgramPolicy = useCallback(async (programId: number) => {
    try {
      const data = await privacyAPI.getProgramPrivacyPolicy(organizationId, programId);
      setProgramPolicy(data);
    } catch (err: any) {
      console.error('Error fetching program privacy policy:', err);
    }
  }, [organizationId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchOrgPolicy(), fetchProgramSummary()]).finally(() => setLoading(false));
  }, [fetchOrgPolicy, fetchProgramSummary]);

  useEffect(() => {
    if (selectedProgramId) {
      fetchProgramPolicy(selectedProgramId);
    } else {
      setProgramPolicy(null);
    }
  }, [selectedProgramId, fetchProgramPolicy]);

  // -----------------------------------------------------------------------
  // ORG POLICY UPDATE
  // -----------------------------------------------------------------------

  const updateOrgPolicy = async (updates: OrgPrivacyPolicyUpdate) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updated = await privacyAPI.updateOrgPrivacyPolicy(organizationId, updates);
      setOrgPolicy(updated);
      if ('idle_timeout_minutes' in updates) {
        // Apply the new inactivity timeout to this session (and, via the shared
        // cache, to this browser's other WeaveIn windows) without a reload.
        window.dispatchEvent(new Event(IDLE_TIMEOUT_CHANGED_EVENT));
      }
      setSuccess('Organization privacy policy updated');
      // Refresh program summary since org changes may affect effective policies
      fetchProgramSummary();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to update policy';
      setError(detail);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------------------
  // PROGRAM POLICY UPDATE
  // -----------------------------------------------------------------------

  const updateProgramPolicy = async (programId: number, updates: ProgramPrivacyPolicyUpdate) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updated = await privacyAPI.updateProgramPrivacyPolicy(organizationId, programId, updates);
      setProgramPolicy(updated);
      setSuccess('Program privacy policy updated');
      fetchProgramSummary();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to update program policy';
      setError(detail);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------------------
  // RENDER
  // -----------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500 mr-2" />
        <span className="text-gray-600 dark:text-gray-400">Loading privacy settings...</span>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <p className="text-[13px] text-ink-500">
        Configure data shielding and privacy policies for {organizationName}
      </p>

      {/* Status Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
          <span className="text-sm text-green-700 dark:text-green-300">{success}</span>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="organization">
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 mb-6">
          <TabsList className="w-max min-w-full flex flex-nowrap bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
            <TabsTrigger
              value="organization"
              className="whitespace-nowrap text-xs sm:text-sm px-3 sm:px-4 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700"
            >
              <Settings2 className="w-4 h-4 mr-1.5 inline" />
              Organization Defaults
            </TabsTrigger>
            <TabsTrigger
              value="programs"
              className="whitespace-nowrap text-xs sm:text-sm px-3 sm:px-4 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700"
            >
              <Shield className="w-4 h-4 mr-1.5 inline" />
              Per-Program Privacy
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Tab 1: Organization Defaults */}
        <TabsContent value="organization" className="animate-fade-in space-y-6">
          {orgPolicy && (
            <OrgDefaultsTab
              organizationId={organizationId}
              policy={orgPolicy}
              saving={saving}
              onUpdate={updateOrgPolicy}
            />
          )}
        </TabsContent>

        {/* Tab 2: Per-Program Privacy */}
        <TabsContent value="programs" className="animate-fade-in space-y-6">
          <ProgramPrivacyTab
            orgPolicy={orgPolicy}
            programs={programSummary}
            selectedProgramId={selectedProgramId}
            programPolicy={programPolicy}
            saving={saving}
            onSelectProgram={setSelectedProgramId}
            onUpdateProgram={updateProgramPolicy}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// TAB 1: ORGANIZATION DEFAULTS
// ============================================================================

function OrgDefaultsTab({
  organizationId,
  policy,
  saving,
  onUpdate,
}: {
  organizationId: number;
  policy: OrgPrivacyPolicy;
  saving: boolean;
  onUpdate: (updates: OrgPrivacyPolicyUpdate) => Promise<void>;
}) {
  return (
    <div className="space-y-6">
      {/* Data Shield Master Toggle */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <CardTitle className="text-lg">Data Shield</CardTitle>
            </div>
            <Switch
              checked={policy.data_shield_enabled}
              onCheckedChange={(checked) => onUpdate({ data_shield_enabled: checked })}
              disabled={saving}
            />
          </div>
          <CardDescription>
            When enabled, sensitive member fields are masked at the point of display. Staff must
            re-authenticate to reveal shielded data.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Sensitivity Threshold */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <CardTitle className="text-lg">Shield Tier Threshold</CardTitle>
          </div>
          <CardDescription>
            Fields at or above this tier will be shielded. A lower threshold shields more fields.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SENSITIVITY_TIERS.map((tier) => (
              <button
                key={tier.key}
                onClick={() => onUpdate({ shield_tier_threshold: tier.key })}
                disabled={saving}
                className={`p-3 rounded-lg border text-left transition-all ${
                  policy.shield_tier_threshold === tier.key
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                } ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                  {tier.label}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {tier.description}
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Inactivity sign-out (web idle timeout) */}
      <IdleTimeoutCard policy={policy} saving={saving} onUpdate={onUpdate} />

      {/* Re-auth & Timeout */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <CardTitle className="text-lg">Reveal Session</CardTitle>
          </div>
          <CardDescription>
            How long revealed data stays visible after re-authentication.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
              Timeout (minutes):
            </label>
            <select
              value={policy.reauth_timeout_minutes}
              onChange={(e) =>
                onUpdate({ reauth_timeout_minutes: parseInt(e.target.value, 10) })
              }
              disabled={saving}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {[5, 10, 15, 30, 60].map((m) => (
                <option key={m} value={m}>
                  {m} min
                </option>
              ))}
            </select>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Require access justification
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Staff must provide a reason when revealing shielded data
              </p>
            </div>
            <Switch
              checked={policy.require_access_justification}
              onCheckedChange={(checked) =>
                onUpdate({ require_access_justification: checked })
              }
              disabled={saving}
            />
          </div>
        </CardContent>
      </Card>

      {/* Break Glass & Audit */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <CardTitle className="text-lg">Emergency Access &amp; Audit</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <PolicyToggle
            label="Break glass emergency access"
            description="Allow admins to bypass shielding in emergencies with full audit trail"
            checked={policy.break_glass_enabled}
            onChange={(checked) => onUpdate({ break_glass_enabled: checked })}
            disabled={saving}
          />
          <PolicyToggle
            label="Enhanced audit logging"
            description="Log all access to shielded fields with IP, user agent, and session details"
            checked={policy.enhanced_audit_enabled}
            onChange={(checked) => onUpdate({ enhanced_audit_enabled: checked })}
            disabled={saving}
          />
        </CardContent>
      </Card>

      {/* Encryption & External Access */}
      <EncryptionManagementCard
        organizationId={organizationId}
        policy={policy}
        saving={saving}
        onUpdate={onUpdate}
      />

      {/* Program Override Control */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <CardTitle className="text-lg">Program-Level Overrides</CardTitle>
          </div>
          <CardDescription>
            When enabled, individual programs can customize privacy settings (can only be <em>more</em>{' '}
            restrictive than these organization defaults).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PolicyToggle
            label="Allow program overrides"
            description="Programs can set stricter thresholds, shorter timeouts, and additional requirements"
            checked={policy.allow_program_overrides}
            onChange={(checked) => onUpdate({ allow_program_overrides: checked })}
            disabled={saving}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// TAB 2: PER-PROGRAM PRIVACY
// ============================================================================

function ProgramPrivacyTab({
  orgPolicy,
  programs,
  selectedProgramId,
  programPolicy,
  saving,
  onSelectProgram,
  onUpdateProgram,
}: {
  orgPolicy: OrgPrivacyPolicy | null;
  programs: ProgramPrivacySummaryItem[];
  selectedProgramId: number | null;
  programPolicy: ProgramPrivacyPolicy | null;
  saving: boolean;
  onSelectProgram: (id: number | null) => void;
  onUpdateProgram: (programId: number, updates: ProgramPrivacyPolicyUpdate) => Promise<void>;
}) {
  if (!orgPolicy?.allow_program_overrides) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Info className="w-8 h-8 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">
            Program-level privacy overrides are disabled for this organization.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
            Enable "Allow program overrides" in the Organization Defaults tab to configure per-program settings.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Program List */}
      <div className="lg:col-span-1">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Programs ({programs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[500px] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
              {programs.map((prog) => (
                <button
                  key={prog.program_id}
                  onClick={() => onSelectProgram(prog.program_id)}
                  className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                    selectedProgramId === prog.program_id
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800 border-l-2 border-transparent'
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {prog.program_name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {prog.effective_policy.data_shield_enabled ? (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                          <Shield className="w-3 h-3" />
                          Shielded
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">No shield</span>
                      )}
                      {prog.program_type && (
                        <span className="text-xs text-gray-400">
                          {prog.program_type}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </button>
              ))}
              {programs.length === 0 && (
                <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No programs found
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Program Detail */}
      <div className="lg:col-span-2">
        {selectedProgramId && programPolicy ? (
          <ProgramPolicyCard
            orgPolicy={orgPolicy}
            programPolicy={programPolicy}
            saving={saving}
            onUpdate={(updates) => onUpdateProgram(selectedProgramId, updates)}
          />
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Shield className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">
                Select a program to view or configure its privacy policy overrides.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// PROGRAM POLICY CARD
// ============================================================================

function ProgramPolicyCard({
  orgPolicy,
  programPolicy,
  saving,
  onUpdate,
}: {
  orgPolicy: OrgPrivacyPolicy;
  programPolicy: ProgramPrivacyPolicy;
  saving: boolean;
  onUpdate: (updates: ProgramPrivacyPolicyUpdate) => Promise<void>;
}) {
  const isOverridden = (field: keyof ProgramPrivacyPolicy) =>
    programPolicy[field] !== null && programPolicy[field] !== undefined;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">{programPolicy.program_name}</CardTitle>
            <CardDescription>
              Settings that are <em>null</em> inherit from the organization default. Overrides can only
              be more restrictive.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Data Shield Override */}
        <OverridableToggle
          label="Data Shield"
          orgValue={orgPolicy.data_shield_enabled}
          programValue={programPolicy.data_shield_enabled}
          onChange={(val) => onUpdate({ data_shield_enabled: val })}
          onReset={() => onUpdate({ data_shield_enabled: null })}
          saving={saving}
        />

        {/* Tier Threshold Override */}
        <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Shield Tier Threshold
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Org default: {TIER_LABELS[orgPolicy.shield_tier_threshold]}
              </p>
            </div>
            {isOverridden('shield_tier_threshold') && (
              <button
                onClick={() => onUpdate({ shield_tier_threshold: null })}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                disabled={saving}
              >
                Reset to inherited
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {SENSITIVITY_TIERS.map((tier) => {
              // Programs can only set threshold <= org threshold (more restrictive)
              const orgTierOrder = SENSITIVITY_TIERS.find(
                (t) => t.key === orgPolicy.shield_tier_threshold
              )?.sortOrder ?? 3;
              const isDisabled = tier.sortOrder > orgTierOrder || saving;
              const currentValue =
                programPolicy.shield_tier_threshold ?? orgPolicy.shield_tier_threshold;

              return (
                <button
                  key={tier.key}
                  onClick={() => onUpdate({ shield_tier_threshold: tier.key })}
                  disabled={isDisabled}
                  className={`p-2 rounded-lg border text-left text-xs transition-all ${
                    currentValue === tier.key
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500'
                      : isDisabled
                        ? 'border-gray-100 dark:border-gray-800 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 cursor-pointer'
                  }`}
                >
                  <span className="font-medium">{tier.label}</span>
                  {isOverridden('shield_tier_threshold') &&
                    programPolicy.shield_tier_threshold === tier.key && (
                      <span className="ml-1 text-blue-500 text-[10px]">custom</span>
                    )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Timeout Override */}
        <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Reveal Timeout
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Org default: {orgPolicy.reauth_timeout_minutes} min
              </p>
            </div>
            {isOverridden('reauth_timeout_minutes') && (
              <button
                onClick={() => onUpdate({ reauth_timeout_minutes: null })}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                disabled={saving}
              >
                Reset to inherited
              </button>
            )}
          </div>
          <select
            value={programPolicy.reauth_timeout_minutes ?? orgPolicy.reauth_timeout_minutes}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              onUpdate({ reauth_timeout_minutes: val });
            }}
            disabled={saving}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500"
          >
            {[5, 10, 15, 30, 60]
              .filter((m) => m <= orgPolicy.reauth_timeout_minutes)
              .map((m) => (
                <option key={m} value={m}>
                  {m} min{m === orgPolicy.reauth_timeout_minutes ? ' (org default)' : ''}
                </option>
              ))}
          </select>
        </div>

        {/* Boolean Overrides */}
        <div className="border-t border-gray-100 dark:border-gray-700 pt-4 space-y-4">
          <OverridableToggle
            label="Require access justification"
            orgValue={orgPolicy.require_access_justification}
            programValue={programPolicy.require_access_justification}
            onChange={(val) => onUpdate({ require_access_justification: val })}
            onReset={() => onUpdate({ require_access_justification: null })}
            saving={saving}
          />
          <OverridableToggle
            label="Enhanced audit logging"
            orgValue={orgPolicy.enhanced_audit_enabled}
            programValue={programPolicy.enhanced_audit_enabled}
            onChange={(val) => onUpdate({ enhanced_audit_enabled: val })}
            onReset={() => onUpdate({ enhanced_audit_enabled: null })}
            saving={saving}
          />
          <OverridableToggle
            label="External key exchange"
            orgValue={orgPolicy.external_key_exchange_enabled}
            programValue={programPolicy.external_key_exchange_enabled}
            onChange={(val) => onUpdate({ external_key_exchange_enabled: val })}
            onReset={() => onUpdate({ external_key_exchange_enabled: null })}
            saving={saving}
          />
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// ENCRYPTION MANAGEMENT CARD
// ============================================================================

function EncryptionManagementCard({
  organizationId,
  policy,
  saving,
  onUpdate,
}: {
  organizationId: number;
  policy: OrgPrivacyPolicy;
  saving: boolean;
  onUpdate: (updates: OrgPrivacyPolicyUpdate) => Promise<void>;
}) {
  const { fmt } = useFormattedDate();
  const [keyStatus, setKeyStatus] = useState<EncryptionKeyStatus | null>(null);
  const [keyLoading, setKeyLoading] = useState(false);
  const [kmsArnInput, setKmsArnInput] = useState('');
  const [initLoading, setInitLoading] = useState(false);
  const [rotateLoading, setRotateLoading] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [encError, setEncError] = useState<string | null>(null);
  const [encSuccess, setEncSuccess] = useState<string | null>(null);

  const fetchKeyStatus = useCallback(async () => {
    if (!policy.per_org_encryption_enabled) return;
    setKeyLoading(true);
    try {
      const status = await privacyAPI.getEncryptionStatus(organizationId);
      setKeyStatus(status);
    } catch {
      // Org may not have initialized encryption yet
      setKeyStatus(null);
    } finally {
      setKeyLoading(false);
    }
  }, [organizationId, policy.per_org_encryption_enabled]);

  useEffect(() => {
    if (policy.per_org_encryption_enabled) {
      fetchKeyStatus();
    } else {
      setKeyStatus(null);
    }
  }, [policy.per_org_encryption_enabled, fetchKeyStatus]);

  const handleInitialize = async () => {
    if (!kmsArnInput.trim().startsWith('arn:aws:kms:')) {
      setEncError('KMS ARN must start with "arn:aws:kms:"');
      return;
    }
    setInitLoading(true);
    setEncError(null);
    try {
      const result = await privacyAPI.initializeEncryption(organizationId, {
        kms_key_arn: kmsArnInput.trim(),
      });
      if (result.success) {
        setEncSuccess('Encryption key initialized successfully');
        setKmsArnInput('');
        fetchKeyStatus();
        setTimeout(() => setEncSuccess(null), 3000);
      } else {
        setEncError(result.message);
      }
    } catch (err: any) {
      setEncError(err.response?.data?.detail || 'Failed to initialize encryption key');
    } finally {
      setInitLoading(false);
    }
  };

  const handleRotate = async () => {
    setRotateLoading(true);
    setEncError(null);
    setShowRotateConfirm(false);
    try {
      const result = await privacyAPI.rotateEncryptionKey(organizationId);
      if (result.success) {
        setEncSuccess(`Key rotated to version ${result.key_version}`);
        fetchKeyStatus();
        setTimeout(() => setEncSuccess(null), 3000);
      } else {
        setEncError(result.message);
      }
    } catch (err: any) {
      setEncError(err.response?.data?.detail || 'Failed to rotate encryption key');
    } finally {
      setRotateLoading(false);
    }
  };

  const handleRevoke = async () => {
    setRevokeLoading(true);
    setEncError(null);
    setShowRevokeConfirm(false);
    try {
      const result = await privacyAPI.revokeEncryptionKey(organizationId);
      if (result.success) {
        setEncSuccess('Encryption key revoked');
        fetchKeyStatus();
        setTimeout(() => setEncSuccess(null), 3000);
      } else {
        setEncError(result.message);
      }
    } catch (err: any) {
      setEncError(err.response?.data?.detail || 'Failed to revoke encryption key');
    } finally {
      setRevokeLoading(false);
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'rotating': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'revoked': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-green-600 dark:text-green-400" />
          <CardTitle className="text-lg">Encryption &amp; External Access</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Per-org encryption toggle */}
        <PolicyToggle
          label="Per-organization encryption"
          description="Use a dedicated AWS KMS key for this organization's data encryption"
          checked={policy.per_org_encryption_enabled}
          onChange={(checked) => onUpdate({ per_org_encryption_enabled: checked })}
          disabled={saving}
        />

        {/* Encryption key management (shown when per-org encryption is enabled) */}
        {policy.per_org_encryption_enabled && (
          <div className="ml-1 mt-2 space-y-3 border-l-2 border-green-200 dark:border-green-800 pl-4">
            {/* Status messages */}
            {encError && (
              <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {encError}
              </div>
            )}
            {encSuccess && (
              <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm text-green-700 dark:text-green-300">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                {encSuccess}
              </div>
            )}

            {keyLoading ? (
              <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading encryption status...
              </div>
            ) : !keyStatus || !keyStatus.initialized ? (
              /* Initialization wizard */
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                  <Key className="w-4 h-4" />
                  <span className="font-medium">No encryption key configured</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Enter your AWS KMS key ARN to generate a dedicated data encryption key for this organization.
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={kmsArnInput}
                    onChange={(e) => setKmsArnInput(e.target.value)}
                    placeholder="arn:aws:kms:us-east-1:123456789:key/..."
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    disabled={initLoading}
                  />
                  <button
                    onClick={handleInitialize}
                    disabled={initLoading || !kmsArnInput.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap"
                  >
                    {initLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Initializing...
                      </>
                    ) : (
                      <>
                        <Key className="w-4 h-4" />
                        Initialize Key
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              /* Key status card */
              <div className="space-y-3">
                {/* Status header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Encryption Key
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(keyStatus.status)}`}>
                      {keyStatus.status}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">v{keyStatus.key_version}</span>
                </div>

                {/* Key details grid */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Algorithm</span>
                    <p className="font-medium text-gray-700 dark:text-gray-300">{keyStatus.algorithm}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Key Version</span>
                    <p className="font-medium text-gray-700 dark:text-gray-300">{keyStatus.key_version}</p>
                  </div>
                  {keyStatus.kms_key_id && (
                    <div className="col-span-2">
                      <span className="text-gray-500 dark:text-gray-400">KMS Key ID</span>
                      <p className="font-mono text-gray-700 dark:text-gray-300 truncate">
                        {keyStatus.kms_key_id}
                      </p>
                    </div>
                  )}
                  {policy.kms_key_arn && (
                    <div className="col-span-2">
                      <span className="text-gray-500 dark:text-gray-400">KMS ARN</span>
                      <p className="font-mono text-gray-700 dark:text-gray-300 truncate">
                        {policy.kms_key_arn}
                      </p>
                    </div>
                  )}
                  {keyStatus.created_at && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Created</span>
                      <p className="font-medium text-gray-700 dark:text-gray-300">
                        {fmt.short(keyStatus.created_at)}
                      </p>
                    </div>
                  )}
                  {keyStatus.last_rotated_at && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Last Rotated</span>
                      <p className="font-medium text-gray-700 dark:text-gray-300">
                        {fmt.short(keyStatus.last_rotated_at)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                {keyStatus.status === 'active' && (
                  <div className="flex gap-2">
                    {!showRotateConfirm ? (
                      <button
                        onClick={() => setShowRotateConfirm(true)}
                        disabled={rotateLoading}
                        className="px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors flex items-center gap-1.5"
                      >
                        <RotateCw className="w-3.5 h-3.5" />
                        Rotate Key
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <span className="text-xs text-amber-700 dark:text-amber-400">
                          Generate a new encryption key version?
                        </span>
                        <button
                          onClick={handleRotate}
                          disabled={rotateLoading}
                          className="px-2 py-1 text-xs font-medium text-white bg-amber-600 rounded hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {rotateLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setShowRotateConfirm(false)}
                          className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:underline"
                        >
                          Cancel
                        </button>
                      </div>
                    )}

                    {!showRevokeConfirm ? (
                      <button
                        onClick={() => setShowRevokeConfirm(true)}
                        disabled={revokeLoading}
                        className="px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors flex items-center gap-1.5"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        Revoke
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <span className="text-xs text-red-700 dark:text-red-400">
                          Revoke key? Data encrypted with this key will be unreadable.
                        </span>
                        <button
                          onClick={handleRevoke}
                          disabled={revokeLoading}
                          className="px-2 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {revokeLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Revoke'}
                        </button>
                        <button
                          onClick={() => setShowRevokeConfirm(false)}
                          className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:underline"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {keyStatus.status === 'revoked' && (
                  <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300">
                    <XCircle className="w-4 h-4 flex-shrink-0" />
                    This key has been revoked. Data encrypted with this key is no longer accessible via per-org encryption. The system will fall back to the global key.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* External key exchange toggle */}
        <PolicyToggle
          label="External key exchange"
          description="Allow controlled data sharing with external systems (HIE/QHIO partners)"
          checked={policy.external_key_exchange_enabled}
          onChange={(checked) =>
            onUpdate({ external_key_exchange_enabled: checked })
          }
          disabled={saving}
        />
      </CardContent>
    </Card>
  );
}

// ============================================================================
// SHARED UI PRIMITIVES
// ============================================================================

/**
 * "Sign out after inactivity" — the org's web idle timeout. Every staff user in
 * this org is signed out of the web app after this many minutes with no
 * activity in any WeaveIn window or tab (lib/sessionIdleTimeout.ts).
 */
function IdleTimeoutCard({
  policy,
  saving,
  onUpdate,
}: {
  policy: OrgPrivacyPolicy;
  saving: boolean;
  onUpdate: (updates: OrgPrivacyPolicyUpdate) => Promise<void>;
}) {
  const { t } = useTranslation();
  const current = policy.idle_timeout_minutes ?? DEFAULT_IDLE_TIMEOUT_MINUTES;
  const configured = policy.idle_timeout_configured ?? false;
  const [draft, setDraft] = useState<string>(String(current));

  useEffect(() => {
    setDraft(String(current));
  }, [current]);

  const parsed = draft.trim() === '' ? NaN : Number(draft);
  const valid = isValidIdleTimeoutMinutes(parsed);
  const dirty = valid && parsed !== current;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <LogOut className="w-5 h-5 text-rose-600 dark:text-rose-400" />
          <CardTitle className="text-lg">
            {t('auth.idleTimeout.settingTitle', 'Inactivity Sign-Out')}
          </CardTitle>
        </div>
        <CardDescription>
          {t(
            'auth.idleTimeout.settingDescription',
            'Staff are signed out of the web app after this many minutes with no activity in any WeaveIn window or tab. A warning appears shortly before sign-out.'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-3">
          <label
            htmlFor="idle-timeout-minutes"
            className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap"
          >
            {t('auth.idleTimeout.settingLabel', 'Sign out after inactivity (minutes)')}
          </label>
          <input
            id="idle-timeout-minutes"
            type="number"
            inputMode="numeric"
            min={MIN_IDLE_TIMEOUT_MINUTES}
            max={MAX_IDLE_TIMEOUT_MINUTES}
            step={1}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={saving}
            aria-invalid={!valid}
            className="w-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="button"
            onClick={() => onUpdate({ idle_timeout_minutes: parsed })}
            disabled={saving || !dirty}
            className="px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('auth.idleTimeout.save', 'Save')}
          </button>
          {configured && (
            <button
              type="button"
              onClick={() => onUpdate({ idle_timeout_minutes: null })}
              disabled={saving}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
            >
              {t('auth.idleTimeout.resetDefault', 'Reset to default ({{minutes}} min)', {
                minutes: DEFAULT_IDLE_TIMEOUT_MINUTES,
              })}
            </button>
          )}
        </div>
        <p
          className={`mt-2 text-xs ${
            valid ? 'text-gray-500 dark:text-gray-400' : 'text-red-600 dark:text-red-400'
          }`}
        >
          {valid
            ? configured
              ? t('auth.idleTimeout.hintConfigured', 'Allowed range: {{min}}–{{max}} minutes.', {
                  min: MIN_IDLE_TIMEOUT_MINUTES,
                  max: MAX_IDLE_TIMEOUT_MINUTES,
                })
              : t(
                  'auth.idleTimeout.hintDefault',
                  'Using the platform default ({{minutes}} minutes = 8 hours). Allowed range: {{min}}–{{max}} minutes.',
                  {
                    minutes: DEFAULT_IDLE_TIMEOUT_MINUTES,
                    min: MIN_IDLE_TIMEOUT_MINUTES,
                    max: MAX_IDLE_TIMEOUT_MINUTES,
                  }
                )
            : t('auth.idleTimeout.invalid', 'Enter a whole number of minutes from {{min}} to {{max}}.', {
                min: MIN_IDLE_TIMEOUT_MINUTES,
                max: MAX_IDLE_TIMEOUT_MINUTES,
              })}
        </p>
      </CardContent>
    </Card>
  );
}

function PolicyToggle({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
        {description && (
          <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
        )}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

function OverridableToggle({
  label,
  orgValue,
  programValue,
  onChange,
  onReset,
  saving,
}: {
  label: string;
  orgValue: boolean;
  programValue: boolean | null;
  onChange: (val: boolean) => void;
  onReset: () => void;
  saving: boolean;
}) {
  const isOverridden = programValue !== null && programValue !== undefined;
  const effectiveValue = isOverridden ? programValue : orgValue;

  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
          {isOverridden ? (
            <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
              custom
            </span>
          ) : (
            <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 rounded">
              inherited
            </span>
          )}
        </p>
        <p className="text-xs text-gray-400">
          Org default: {orgValue ? 'On' : 'Off'}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {isOverridden && (
          <button
            onClick={onReset}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            disabled={saving}
          >
            Reset
          </button>
        )}
        <Switch
          checked={effectiveValue}
          onCheckedChange={onChange}
          disabled={saving}
        />
      </div>
    </div>
  );
}
